import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import { EventProvider } from '@/hooks/useEvent'
import { ToastProvider } from '@/components/ui'
import { PageSpinner } from '@/components/ui'
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/env'

import Login from '@/pages/admin/Login'
import AdminLayout from '@/pages/admin/AdminLayout'
import EventsList from '@/pages/admin/EventsList'
import EventShell from '@/pages/admin/EventShell'
import Dashboard from '@/pages/admin/Dashboard'
import Hotels from '@/pages/admin/Hotels'
import RoomTypesPage from '@/pages/admin/RoomTypes'
import Companies from '@/pages/admin/Companies'
import CompanyDetail from '@/pages/admin/CompanyDetail'
import RoomingList from '@/pages/admin/RoomingList'
import ImportExport from '@/pages/admin/ImportExport'
import Activity from '@/pages/admin/Activity'
import EventSettings from '@/pages/admin/EventSettings'
import CompanyPortal from '@/pages/company/CompanyPortal'

function ConfigError() {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-50 px-6">
      <div className="max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        <p className="font-semibold">Supabase is not configured</p>
        <p className="mt-2">
          Copy <code className="rounded bg-white px-1 py-0.5">.env.example</code> to <code className="rounded bg-white px-1 py-0.5">.env</code> and set{' '}
          <code className="rounded bg-white px-1 py-0.5">VITE_SUPABASE_URL</code> and <code className="rounded bg-white px-1 py-0.5">VITE_SUPABASE_ANON_KEY</code>, then restart the dev
          server. See README.md.
        </p>
      </div>
    </div>
  )
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth()
  if (loading) return <PageSpinner label="Checking your session…" />
  if (!session) return <Navigate to="/admin/login" replace />
  if (!profile || !profile.is_active) {
    return (
      <div className="flex h-screen items-center justify-center px-6 text-center text-sm text-slate-600">
        Your account isn't set up as an admin yet, or has been deactivated. Ask a super admin to check the Profiles table.
      </div>
    )
  }
  return <>{children}</>
}

export default function App() {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return <ConfigError />

  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/login" element={<Login />} />

            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <AdminLayout />
                </RequireAuth>
              }
            >
              <Route index element={<EventsList />} />
              <Route path="events" element={<EventsList />} />
              <Route
                path="events/:eventId"
                element={
                  <EventProvider>
                    <EventShell />
                  </EventProvider>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="hotels" element={<Hotels />} />
                <Route path="room-types" element={<RoomTypesPage />} />
                <Route path="companies" element={<Companies />} />
                <Route path="companies/:companyId" element={<CompanyDetail />} />
                <Route path="rooming-list" element={<RoomingList />} />
                <Route path="import-export" element={<ImportExport />} />
                <Route path="activity" element={<Activity />} />
                <Route path="settings" element={<EventSettings />} />
              </Route>
            </Route>

            <Route path="/company/:token" element={<CompanyPortal />} />
            <Route path="/company/:token/update" element={<CompanyPortal showUpdateCodePrompt />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  )
}
